import { whatsappAccessLostError } from '@/lib/errors/errorCatalog';

export type HistorySyncStatus =
  | 'REQUESTED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'FAILED'
  | 'REFUSED';

export const RE_ONBOARD_STEPS =
  'On your phone, open WhatsApp Business → Settings → Account → Business Platform → Disconnect Account. Then connect this number here again. Chat history can be imported only once per connection.';

export const CONNECTION_CHECK_STEPS =
  "Check the phone's internet connection and keep WhatsApp Business open. The rest will arrive as the connection allows.";

export const HISTORY_SYNC_WINDOW_HOURS = 24;
export const HISTORY_SYNC_WINDOW_MS = HISTORY_SYNC_WINDOW_HOURS * 60 * 60 * 1000;

const IN_FLIGHT_STATUSES = new Set<string>(['REQUESTED', 'IN_PROGRESS']);

export const HISTORY_SYNC_STALE_MS = 15 * 60 * 1000;

export interface HistorySyncAccountLike {
  id?: string | null;
  connection_type?: string | null;
  coexistence_onboarded_at?: string | null;
  history_sync_status?: string | null;
  history_sync_requested_at?: string | null;
  history_sync_progress?: number | null;
  history_sync_progress_at?: string | null;
  history_sync_completed_at?: string | null;
  history_sync_error?: string | null;
  history_sync_dismissed_key?: string | null;
}

export function toDateOrNull(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const d = new Date(trimmed);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function resolveHistorySyncProgress(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw !== 'number' && typeof raw !== 'string') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.min(100, Math.max(0, Math.round(n)));
}

export interface HistorySyncWindow {
  known: boolean;
  expiresAt: Date | null;
  msRemaining: number | null;
  expired: boolean;
}

export function resolveHistorySyncWindow(
  onboardedAt: unknown,
  now: Date = new Date()
): HistorySyncWindow {
  const started = toDateOrNull(onboardedAt);
  if (!started) {
    return { known: false, expiresAt: null, msRemaining: null, expired: false };
  }
  const expiresAt = new Date(started.getTime() + HISTORY_SYNC_WINDOW_MS);
  const msRemaining = expiresAt.getTime() - now.getTime();
  return {
    known: true,
    expiresAt,
    msRemaining: Math.max(0, msRemaining),
    expired: msRemaining <= 0,
  };
}

export function formatWindowRemaining(ms: number | null | undefined): string | null {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return null;
  if (ms <= 0) return null;
  const totalMinutes = Math.floor(ms / 60000);
  if (totalMinutes < 1) return 'under a minute';
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes}m`;
  if (minutes <= 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

function joinSentences(...parts: Array<string | null | undefined>): string {
  return parts
    .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
    .map((p) => {
      const t = p.trim();
      return /[.!?]$/.test(t) ? t : `${t}.`;
    })
    .join(' ');
}

function isRateLimitText(detail?: string | null): boolean {
  if (typeof detail !== 'string') return false;
  return /request limit reached|rate limit|\(#4\)|error 4/i.test(detail);
}

export function isHistorySyncRunning(status?: string | null): boolean {
  return typeof status === 'string' && IN_FLIGHT_STATUSES.has(status);
}

export function isHistorySyncComplete(status?: string | null): boolean {
  return status === 'COMPLETED';
}

export interface HistorySyncStaleness {
  stale: boolean;
  sinceMs: number | null;
  measuredFrom: 'progress' | 'request' | null;
}

export function resolveHistorySyncStaleness(
  account: HistorySyncAccountLike | null | undefined,
  now: Date = new Date()
): HistorySyncStaleness {
  if (!account || !isHistorySyncRunning(account.history_sync_status)) {
    return { stale: false, sinceMs: null, measuredFrom: null };
  }
  const lastProgress = toDateOrNull(account.history_sync_progress_at);
  const asked = toDateOrNull(account.history_sync_requested_at);
  const anchor = lastProgress || asked;
  if (!anchor) return { stale: false, sinceMs: null, measuredFrom: null };

  const sinceMs = Math.max(0, now.getTime() - anchor.getTime());
  return {
    stale: sinceMs >= HISTORY_SYNC_STALE_MS,
    sinceMs,
    measuredFrom: lastProgress ? 'progress' : 'request',
  };
}

export function isHistorySyncActivelyImporting(
  account: HistorySyncAccountLike | null | undefined,
  now: Date = new Date()
): boolean {
  if (!account || !isHistorySyncRunning(account.history_sync_status)) return false;
  return !resolveHistorySyncStaleness(account, now).stale;
}

export const HISTORY_SYNC_WATCH_LIMIT_MS = 2 * 60 * 60 * 1000;

export type HistorySyncPollTier = 'fast' | 'slow' | 'off';

export function resolveHistorySyncPollTier(
  account: HistorySyncAccountLike | null | undefined,
  now: Date = new Date()
): HistorySyncPollTier {
  if (!account || !isHistorySyncRunning(account.history_sync_status)) return 'off';

  const staleness = resolveHistorySyncStaleness(account, now);
  if (staleness.sinceMs === null) return 'off';
  if (!staleness.stale) return 'fast';
  return staleness.sinceMs < HISTORY_SYNC_WATCH_LIMIT_MS ? 'slow' : 'off';
}

export function historySyncNeedsClock(
  account: HistorySyncAccountLike | null | undefined,
  now: Date = new Date()
): boolean {
  if (!account || !isHistorySyncEligible(account.connection_type)) return false;
  if (isHistorySyncComplete(account.history_sync_status)) return false;

  const syncWindow = resolveHistorySyncWindow(account.coexistence_onboarded_at, now);
  if (syncWindow.known && !syncWindow.expired) return true;

  return resolveHistorySyncPollTier(account, now) !== 'off';
}

export function formatStalledFor(ms: number | null | undefined): string | null {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return null;
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return null;
  if (minutes < 60) return `for ${minutes} minute${minutes === 1 ? '' : 's'}`;
  return 'for over an hour';
}

export function isHistorySyncEligible(connectionType?: string | null): boolean {
  return connectionType === 'coexistence';
}

export type HistorySyncTone = 'info' | 'warning' | 'error';

export interface HistorySyncNotice {
  tone: HistorySyncTone;
  headline: string;
  guidance: string;
  showSync: boolean;
  syncLabel: string;
  progress: number | null;
  remaining: string | null;
  window: HistorySyncWindow;
  status: HistorySyncStatus | null;
  // Set only on FAILURE notices (stuck / refused / failed). Names this one
  // occurrence, so closing it hides it for good while a later failure still
  // shows once. Countdown notices never get one: hiding them for good could
  // let Meta's 24-hour window lapse unseen.
  dismissKey?: string | null;
  // A page that fixes the cause (e.g. reconnect WhatsApp). Shown as a button
  // in place of the retry, which can't succeed until the cause is fixed.
  fixAction?: { label: string; to: string } | null;
}

export function historySyncDismissKey(
  account: HistorySyncAccountLike,
  kind: 'stuck' | 'REFUSED' | 'FAILED'
): string | null {
  if (!account.id) return null;
  const asked = toDateOrNull(account.history_sync_requested_at);
  return `history-sync:${account.id}:${kind}:${asked ? asked.getTime() : 'none'}`;
}

export function isHistorySyncNoticeDismissed(
  account: HistorySyncAccountLike | null | undefined,
  notice: HistorySyncNotice | null | undefined
): boolean {
  const key = notice?.dismissKey;
  return !!key && account?.history_sync_dismissed_key === key;
}

export function buildHistorySyncNotice(
  account: HistorySyncAccountLike | null | undefined,
  now: Date = new Date()
): HistorySyncNotice | null {
  if (!account) return null;
  if (!isHistorySyncEligible(account.connection_type)) return null;

  const status = (typeof account.history_sync_status === 'string'
    ? account.history_sync_status
    : null) as HistorySyncStatus | null;

  if (isHistorySyncComplete(status)) return null;

  const syncWindow = resolveHistorySyncWindow(account.coexistence_onboarded_at, now);

  const windowClosed = syncWindow.known && syncWindow.expired;

  const remaining = formatWindowRemaining(syncWindow.msRemaining);
  const progress = resolveHistorySyncProgress(account.history_sync_progress);

  const staleness = resolveHistorySyncStaleness(account, now);
  if (staleness.stale) {
    const stalledFor = formatStalledFor(staleness.sinceMs);
    return {
      tone: 'warning',
      headline:
        progress === null
          ? 'Chat history import seems stuck'
          : `Chat history import seems stuck at ${progress}%`,
      guidance:
        (progress === null
          ? `No chats have arrived${stalledFor ? ` ${stalledFor}` : ''}. `
          : `${progress}% arrived, then nothing${stalledFor ? ` ${stalledFor}` : ' for a while'}. Chats already imported are safe in your inbox. `) +
        `${CONNECTION_CHECK_STEPS} ` +
        `If it still doesn't move: ${RE_ONBOARD_STEPS}`,
      showSync: false,
      syncLabel: '',
      progress: null,
      remaining,
      window: syncWindow,
      status,
      dismissKey: historySyncDismissKey(account, 'stuck'),
    };
  }

  if (isHistorySyncRunning(status)) {
    return {
      tone: 'info',
      headline:
        progress === null
          ? 'Importing chat history from the WhatsApp Business app'
          : `Importing chat history — ${progress}% done`,
      guidance:
        'Chats arrive in batches and can take several minutes. Please keep WhatsApp Business open on the phone until it finishes.',
      showSync: false,
      syncLabel: 'Syncing…',
      progress,
      remaining,
      window: syncWindow,
      status,
    };
  }

  const detail =
    typeof account.history_sync_error === 'string' && account.history_sync_error.trim()
      ? account.history_sync_error.trim()
      : null;
  const accessLostError = whatsappAccessLostError(detail);
  const accessLost = accessLostError?.description ?? null;
  const fixAction = accessLostError?.action ?? null;

  if (status === 'REFUSED') {
    return {
      tone: 'error',
      headline: accessLostError ? accessLostError.title : "We couldn't start the chat history import",
      guidance: accessLost
        ? accessLost
        : windowClosed
        ? joinSentences(
            detail ? `WhatsApp said: ${detail}` : "The request didn't reach WhatsApp",
            "The 24-hour import window has since closed, so you can't try again",
          ) + ` To import chat history: ${RE_ONBOARD_STEPS}`
        : joinSentences(
            detail ? `WhatsApp said: ${detail}` : "The request didn't reach WhatsApp",
            isRateLimitText(detail)
              ? "WhatsApp's request limit was reached. Please wait 15-30 minutes and try again"
              : 'Please try again',
            remaining ? `You have about ${remaining} left to import` : null,
          ),
      showSync: !windowClosed && !fixAction,
      syncLabel: windowClosed || fixAction ? '' : 'Try again',
      fixAction,
      progress: null,
      remaining,
      window: syncWindow,
      status,
      dismissKey: historySyncDismissKey(account, 'REFUSED'),
    };
  }

  if (status === 'FAILED') {
    return {
      tone: 'error',
      headline: "We couldn't import your chat history",
      guidance: accessLost
        ? accessLost
        : (detail ? `WhatsApp said: ${detail} ` : 'WhatsApp stopped the import. ') +
          `To try again: ${RE_ONBOARD_STEPS}`,
      showSync: false,
      syncLabel: '',
      progress: null,
      remaining,
      window: syncWindow,
      status,
      dismissKey: historySyncDismissKey(account, 'FAILED'),
      fixAction,
    };
  }

  if (!syncWindow.known) {
    return {
      tone: 'info',
      headline: 'Import chat history from the WhatsApp Business app',
      guidance:
        "You can import phone chats once, within 24 hours of connecting. We don't have a record of when that started, so try it now.",
      showSync: true,
      syncLabel: 'Sync chat history',
      progress: null,
      remaining: null,
      window: syncWindow,
      status,
    };
  }

  if (windowClosed) return null;

  const urgent = (syncWindow.msRemaining ?? 0) <= 2 * 60 * 60 * 1000;
  return {
    tone: urgent ? 'warning' : 'info',
    headline: urgent
      ? `Only ${remaining} left to import chat history`
      : `Import chat history — ${remaining} left`,
    guidance:
      "This number uses the WhatsApp Business app and WeNext together. You can import the phone's chats once, within 24 hours of connecting. This covers the last 180 days of one-to-one chats, not group chats. After that, it can't be imported.",
    showSync: true,
    syncLabel: 'Sync chat history',
    progress: null,
    remaining,
    window: syncWindow,
    status,
  };
}

export function describeHistorySyncState(
  account: HistorySyncAccountLike | null | undefined,
  now: Date = new Date()
): string | null {
  if (!account || !isHistorySyncEligible(account.connection_type)) return null;

  const status = account.history_sync_status;
  const progress = resolveHistorySyncProgress(account.history_sync_progress);

  if (isHistorySyncComplete(status)) return 'Chat history imported';
  if (isHistorySyncRunning(status)) {
    if (resolveHistorySyncStaleness(account, now).stale) {
      return progress === null ? 'Import stuck — nothing arrived' : `Import stuck at ${progress}%`;
    }
    return progress === null ? 'Importing chat history…' : `Importing chat history — ${progress}%`;
  }
  if (status === 'REFUSED') return 'Chat history import was refused by WhatsApp';
  if (status === 'FAILED') return 'Chat history import failed. Please reconnect';

  const syncWindow = resolveHistorySyncWindow(account.coexistence_onboarded_at, now);
  if (syncWindow.known && syncWindow.expired) return 'Chat history import window closed';
  const remaining = formatWindowRemaining(syncWindow.msRemaining);
  if (remaining) return `Chat history not imported — ${remaining} left`;
  return 'Chat history not imported';
}

export function describeHistorySyncRefusal(code: unknown): string {
  switch (code) {
    case 'window_expired':
      return "The 24-hour import window has closed. Chat history can't be imported now.";
    case 'already_completed':
      return 'This account\'s chat history has already been imported.';
    case 'in_flight':
      return 'An import is already running. Please wait a few minutes.';
    case 'rate_limited':
      return "WhatsApp's request limit was reached. Wait 15-30 minutes before trying again. Retrying sooner makes the wait longer.";
    default:
      return "We couldn't import chat history right now. Please try again.";
  }
}
